(function () {
  const config = window.discountGiftConfig;

  if (!config || !Array.isArray(config.campaigns) || !config.campaigns.length) return;

  const giftPropertyKey = '_discount_gift_code';
  const autoGiftPropertyKey = '_discount_gift_auto_added';
  const preferredCodeStorageKey = 'dih_preferred_discount_gift_code';
  const nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  if (!nativeFetch) return;

  const campaigns = config.campaigns.map((campaign) => ({
    code: String(campaign.code || '').toUpperCase(),
    variantId: String(campaign.variantId || '')
  })).filter((campaign) => campaign.code && campaign.variantId);
  const campaignCodes = campaigns.map((campaign) => campaign.code);
  const giftVariantIds = campaigns.map((campaign) => campaign.variantId);
  let syncing = false;
  let queued = false;

  function getRoutes() {
    return window.routes || {
      cart_add_url: '/cart/add.js',
      cart_change_url: '/cart/change.js',
      cart_url: '/cart.js'
    };
  }

  function normalizeCode(value) {
    const code = String(value || '').trim().toUpperCase();
    return campaignCodes.includes(code) ? code : null;
  }

  function collectCode(value, foundCodes) {
    const text = String(value || '');
    campaignCodes.forEach((code) => {
      if (text.toUpperCase().includes(code)) foundCodes.add(code);
    });
  }

  function getCodesFromDiscountMarkers() {
    const foundCodes = new Set();

    document.querySelectorAll('[data-discount-gift-active-code]').forEach((element) => {
      collectCode(element.getAttribute('data-discount-gift-active-code'), foundCodes);
    });

    return Array.from(foundCodes);
  }

  function getCodesFromLocation() {
    const foundCodes = new Set();
    const pathMatch = window.location.pathname.match(/\/discount\/([^/?#]+)/i);

    if (pathMatch) collectCode(decodeURIComponent(pathMatch[1]), foundCodes);

    const params = new URLSearchParams(window.location.search);
    ['discount', 'discount_code'].forEach((paramName) => {
      collectCode(params.get(paramName), foundCodes);
    });

    return Array.from(foundCodes);
  }

  function getRawDiscountCodesFromLocation() {
    const rawCodes = [];
    const pathMatch = window.location.pathname.match(/\/discount\/([^/?#]+)/i);

    if (pathMatch) rawCodes.push(decodeURIComponent(pathMatch[1]).trim().toUpperCase());

    const params = new URLSearchParams(window.location.search);
    ['discount', 'discount_code'].forEach((paramName) => {
      const value = params.get(paramName);
      if (value) rawCodes.push(String(value).trim().toUpperCase());
    });

    return rawCodes;
  }

  function syncPreferredCodeFromLocation(campaignLocationCodes) {
    const rawCodes = getRawDiscountCodesFromLocation();

    // No discount in the URL -> keep whatever code was remembered earlier.
    if (!rawCodes.length) return;

    const campaignCode = (campaignLocationCodes || []).map(normalizeCode).filter(Boolean).pop();

    try {
      if (campaignCode) {
        // Remember the gift code so the gift survives navigation to pages
        // that no longer carry the ?discount= param.
        sessionStorage.setItem(preferredCodeStorageKey, campaignCode);
      } else {
        // The link carries a non-gift code (e.g. DOGDAD) -> drop any remembered
        // gift code so a previously armed gift does not linger.
        sessionStorage.removeItem(preferredCodeStorageKey);
      }
    } catch (error) {
      // Session storage unavailable; persistence falls back to cart/URL detection.
    }
  }

  function getCodesFromCart(cart) {
    const foundCodes = new Set();

    // `cart.discount_codes` also keeps codes Shopify has REJECTED, flagged
    // applicable:false. They linger after the shopper applies a different code
    // (e.g. MOLD30 stays listed once LABORDAY is applied), and counting them
    // made this function report the old gift code as still active - so the free
    // gift was never removed and went through checkout free. Verified 2026-09-01.
    // Only trust entries Shopify actually applied.
    (cart.discount_codes || []).forEach((discount) => {
      if (!discount || discount.applicable === false) return;
      collectCode(discount.code, foundCodes);
      collectCode(discount.title, foundCodes);
    });

    const discountCollections = [
      cart.cart_level_discount_applications,
      cart.discount_applications
    ];

    discountCollections.forEach((collection) => {
      if (!Array.isArray(collection)) return;

      collection.forEach((discount) => {
        collectCode(discount.code, foundCodes);
        collectCode(discount.title, foundCodes);
      });
    });

    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => {
        collectCode(discount.code, foundCodes);
        collectCode(discount.title, foundCodes);
      });
    });

    return Array.from(foundCodes);
  }

  function getRejectedCodesFromCart(cart) {
    return (cart.discount_codes || [])
      .filter((discount) => discount && discount.applicable === false)
      .map((discount) => normalizeCode(discount.code))
      .filter(Boolean);
  }

  // The remembered code (set when a gift code is typed in the cart box) only
  // stands in while the cart says nothing about it. Once Shopify has rejected
  // it or applied a different code (e.g. a SPOOKY link after TREAT25 was typed),
  // forget it - otherwise the free gift stayed in the cart and the checkout
  // button re-applied the old code over the new one. Verified 2026-10-27.
  function forgetReplacedPreferredCode(cart) {
    let storedCode = null;

    try {
      storedCode = normalizeCode(sessionStorage.getItem(preferredCodeStorageKey));
    } catch (error) {
      return;
    }

    if (!storedCode) return;

    const replaced = (cart.discount_codes || []).some((discount) => {
      if (!discount) return false;
      const code = String(discount.code || '').trim().toUpperCase();
      return discount.applicable === false ? code === storedCode : code !== storedCode;
    });

    if (!replaced) return;

    try {
      sessionStorage.removeItem(preferredCodeStorageKey);
    } catch (error) {
      // Session storage unavailable; nothing to forget.
    }
  }

  function getSelectedCampaign(activeCodeList, rejectedCodes) {
    const rejected = rejectedCodes || [];
    const activeCodes = Array.from(new Set((activeCodeList || []).map(normalizeCode).filter(Boolean)));
    // A code still in the URL that Shopify rejected must not pick the gift.
    const locationCodes = getCodesFromLocation().filter((code) => !rejected.includes(code));
    const preferredLocationCode = locationCodes.reverse().find(Boolean);
    let preferredStoredCode = null;

    try {
      preferredStoredCode = normalizeCode(sessionStorage.getItem(preferredCodeStorageKey));
    } catch (error) {
      preferredStoredCode = null;
    }

    const selectedCode = preferredLocationCode || (activeCodes.includes(preferredStoredCode) && preferredStoredCode) || activeCodes[activeCodes.length - 1] || preferredStoredCode;

    return campaigns.find((campaign) => campaign.code === selectedCode) || null;
  }

  function updateCheckoutDiscountField(campaign) {
    const form = document.getElementById('cart');
    if (!form) return;

    let input = form.querySelector('input[data-discount-gift-checkout-code]');

    if (!campaign) {
      if (input) input.remove();
      return;
    }

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'discount';
      input.setAttribute('data-discount-gift-checkout-code', 'true');
      form.appendChild(input);
    }

    input.value = campaign.code;
  }

  function getCheckoutCampaign() {
    const form = document.getElementById('cart');
    const checkoutCodeInput = form && form.querySelector('input[data-discount-gift-checkout-code]');
    const checkoutCode = checkoutCodeInput && normalizeCode(checkoutCodeInput.value);

    if (checkoutCode) return campaigns.find((campaign) => campaign.code === checkoutCode) || null;

    const selectedCampaign = getSelectedCampaign([
      ...getCodesFromLocation(),
      ...getCodesFromDiscountMarkers()
    ]);

    if (selectedCampaign) return selectedCampaign;

    try {
      const preferredStoredCode = normalizeCode(sessionStorage.getItem(preferredCodeStorageKey));
      return campaigns.find((campaign) => campaign.code === preferredStoredCode) || null;
    } catch (error) {
      return null;
    }
  }

  function installCheckoutDiscountRedirect() {
    const checkoutButton = document.getElementById('checkout');

    if (!checkoutButton || checkoutButton.dataset.discountGiftCheckoutBound === 'true') return;

    checkoutButton.dataset.discountGiftCheckoutBound = 'true';
    checkoutButton.addEventListener('click', (event) => {
      const campaign = getCheckoutCampaign();

      if (!campaign) return;

      event.preventDefault();
      event.stopPropagation();
      // Only the campaign code. A comma-joined URL (previously ',FREESHIP-LM7') is
      // NOT parsed as two codes once either code is expired/inactive - Shopify stores
      // the whole string as one code with applicable:false, which silently zeroed the
      // customer's discount at checkout. Verified 2026-08-20 after FREESHIP-LM7 expired.
      window.location.href = '/discount/' + encodeURIComponent(campaign.code) + '?redirect=/checkout';
    }, true);
  }

  function isAutoGiftLine(item) {
    const properties = item.properties || {};
    return properties[giftPropertyKey] && properties[autoGiftPropertyKey] === 'true' || giftVariantIds.includes(getItemVariantId(item));
  }

  function getItemVariantId(item) {
    return String(item.variant_id || item.id || '');
  }

  function isPaidNonGiftLine(item) {
    const linePrice = Number(item.final_line_price || item.line_price || item.final_price || item.price || 0);

    return !isAutoGiftLine(item) && linePrice > 0;
  }

  function isCartPage() {
    return window.location.pathname.replace(/\/$/, '') === '/cart';
  }

  async function getCart() {
    const response = await nativeFetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) throw new Error('Unable to read cart');

    return response.json();
  }

  async function addGift(campaign) {
    const routes = getRoutes();
    const response = await nativeFetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: campaign.variantId,
        quantity: 1,
        properties: {
          [giftPropertyKey]: campaign.code,
          [autoGiftPropertyKey]: 'true'
        }
      })
    });

    if (!response.ok) return false;

    return true;
  }

  async function changeGift(item, quantity) {
    const routes = getRoutes();
    const response = await nativeFetch(routes.cart_change_url || '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: item.key,
        quantity: quantity
      })
    });

    return response.ok;
  }

  function refreshCartView() {
    if (isCartPage()) {
      window.location.reload();
      return;
    }

    document.dispatchEvent(new CustomEvent('discount-gift:cart-changed'));
  }

  async function syncGifts() {
    if (syncing) {
      queued = true;
      return;
    }

    syncing = true;
    queued = false;

    try {
      const markerCodes = getCodesFromDiscountMarkers();
      const locationCodes = getCodesFromLocation();
      syncPreferredCodeFromLocation(locationCodes);

      const cart = await getCart();
      forgetReplacedPreferredCode(cart);
      const rejectedCodes = getRejectedCodesFromCart(cart);
      const liveLocationCodes = locationCodes.filter((code) => !rejectedCodes.includes(code));
      const cartCodes = getCodesFromCart(cart);
      const activeCodeList = liveLocationCodes.length ? liveLocationCodes : cartCodes.length ? cartCodes : markerCodes;
      const selectedCampaign = getSelectedCampaign(activeCodeList, rejectedCodes);
      updateCheckoutDiscountField(selectedCampaign);
      installCheckoutDiscountRedirect();
      const activeVariantIds = selectedCampaign ? [selectedCampaign.variantId] : [];
      const autoGiftLines = (cart.items || []).filter(isAutoGiftLine);
      const hasPaidNonGiftItem = (cart.items || []).some(isPaidNonGiftLine);
      let cartChanged = false;

      if (!hasPaidNonGiftItem) {
        for (const item of autoGiftLines) {
          cartChanged = await changeGift(item, 0) || cartChanged;
        }

        if (cartChanged) refreshCartView();
        return;
      }

      for (const item of autoGiftLines) {
        if (!activeVariantIds.includes(getItemVariantId(item))) {
          cartChanged = await changeGift(item, 0) || cartChanged;
          continue;
        }

        if (item.quantity !== 1) {
          cartChanged = await changeGift(item, 1) || cartChanged;
        }
      }

      if (selectedCampaign) {
        const giftAlreadyInCart = autoGiftLines.some((item) => getItemVariantId(item) === selectedCampaign.variantId);
        if (!giftAlreadyInCart) {
          cartChanged = await addGift(selectedCampaign) || cartChanged;
        }
      }

      if (cartChanged) refreshCartView();
    } catch (error) {
      return null;
    } finally {
      syncing = false;
      if (queued) window.setTimeout(syncGifts, 250);
    }
  }

  if (nativeFetch) {
    window.fetch = function () {
      const input = arguments[0];
      const options = arguments[1];
      const requestText = [
        input && input.url ? input.url : input,
        options && options.body
      ].filter(Boolean).join(' ');

      return nativeFetch.apply(this, arguments).then((response) => {
        if (/\/cart\/(add|change|update)(\.js)?/i.test(requestText)) {
          window.setTimeout(syncGifts, 500);
        }

        return response;
      });
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGifts);
  } else {
    syncGifts();
  }

  window.setTimeout(syncGifts, 1500);
})();
